import React, { Component } from 'react'
import PropTypes from 'prop-types'
import loader from '../loader.gif'
import submit from '../submit.gif'
import logo from '../logo.gif'

/*
  PGUploadDocuments.js
  Step 16 of the Postgraduate application form.

  Document groups (labels used by SubmitIncompleteApplication):
  ── Academic Documents (mandatory) ──────────────────────────────
    BachelorDegreeCertificate   Official Bachelor's Degree Certificate *
    AcademicTranscript          Official Academic Transcript *
  ── Identification Documents (mandatory) ────────────────────────
    Passport                    Valid Passport Copy *
    NationalID                  National ID (Egyptian applicants) *[Egyptian]
    BirthCertificate            Birth Certificate *
  ── English Language Proficiency (conditional) ──────────────────
    EnglishAssessment           IELTS / PTE / TOEFL / Duolingo certificate  [if not taught in English]
    AdditionalTests             GRE / GMAT / Additional test certificates   [optional]
  ── Personal & Academic Profile (mandatory) ─────────────────────
    PersonalPicture             Passport-Sized Photo *
    CV                          Résumé / CV *
    PersonalStatement           Personal Statement / Statement of Purpose * [help icon]
    LetterOfRecommendation      Letters of Recommendation (min. 2) *       [help icon]
  ── Professional Documents (conditional) ────────────────────────
    ExperienceLetter            Work Experience / Experience Letter         [optional]
    ProfessionalCertificate     Professional Certificates / Diplomas        [mandatory if profExams filled]
*/

const HELP_PERSONAL_STATEMENT = `Your Personal Statement (also called Statement of Purpose) should be a 600–1000 word essay describing:
• Your academic background and research interests
• Why you are applying to this specific programme
• Your career goals and how this programme supports them
• Any relevant professional or research experience`

const HELP_RECOMMENDATION = `Letters of Recommendation must be:
• Written by academic supervisors, professors, or professional managers
• On official letterhead with a signature
• A minimum of TWO letters are required for your application to be complete
• Letters should speak to your academic ability, professional competence, and potential for postgraduate study`

function makeUploadSection (label, typelabel, inputId, disabled, acceptFiles, maxAttachmentSize, files, maxCount, onChange) {
  return (
    <input
      type='file'
      style={{ display: 'none' }}
      multiple={true}
      id={inputId}
      onClick={(e) => {
        e.target.value = null
        if (disabled) e.preventDefault()
      }}
      onChange={(e) => {
        if (disabled) return
        const newFiles = Array.from(e.target.files)
        if (newFiles.length + files.length > maxCount) {
          alert(`You can add up to ${maxCount} files only!`)
          return
        }
        const validFiles = []
        for (const f of newFiles) {
          const ext = '.' + f.name.split('.').pop()
          const acceptArr = acceptFiles.split(',').map(s => s.trim())
          const ok = acceptArr.some(a => a.toLowerCase().includes(ext.toLowerCase()))
          if (!ok) { alert('Please upload a supported file type only!'); return }
          if (Math.round((f.size / 1000000) * 100) / 100 > maxAttachmentSize) {
            alert('Upload Failed — maximum allowed size per attachment was exceeded!')
            return
          }
          f.label = label
          f.typelabel = typelabel
          validFiles.push(f)
        }
        onChange(validFiles)
      }}
      accept={acceptFiles}
    />
  )
}

export default class PGUploadDocuments extends Component {
  constructor (props) {
    super(props)
    this.state = {
      files: [],
      loading: true,
      showModal: false,
      helpModal: null,       // 'personalStatement' | 'recommendation' | null
      error: '',
      submitLoading: false,
      accepts: '',
      acceptFiles: '',
      numberOfUpload: 30,
      maxAttachmentSize: 10,
      maxApplicationAttachmentSize: 50,
      color: 'red',
      disable: false,
      showSuccessModal: false,
      alreadySubmitted: false,
      processingPage: false,
      paymentFailed: false
    }
    this.handleSubmit = this.handleSubmit.bind(this)
    this.toBase64 = this.toBase64.bind(this)
    this.addFiles = this.addFiles.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
    this.validateMandatory = this.validateMandatory.bind(this)
  }

  componentDidMount () {
    fetch(`${process.env.REACT_APP_API_URL}/api/attachment/GetFileTypes`)
      .then(res => res.json())
      .then(res => {
        const accepts = res || []
        let acceptFiles = ''
        accepts.forEach((el, i) => {
          acceptFiles += i === accepts.length - 1 ? el.type : el.type + ','
        })
        this.setState({ accepts, acceptFiles })
      })
      .then(() => fetch(`${process.env.REACT_APP_API_URL}//api/attachment/GetMaxAttachmentNumber`))
      .then(res => res.text())
      .then(res => this.setState({ numberOfUpload: parseInt(res) || 30 }))
      .then(() => fetch(`${process.env.REACT_APP_API_URL}//api/attachment/GetMaxAttachmentSize`))
      .then(res => res.text())
      .then(res => this.setState({ maxAttachmentSize: parseInt(res) || 10 }))
      .then(() => fetch(`${process.env.REACT_APP_API_URL}//api/attachment/GetMaxApplicationAttachmentSize`))
      .then(res => res.text())
      .then(res => this.setState({ maxApplicationAttachmentSize: parseInt(res) || 50 }))
      .then(() => fetch(`${process.env.REACT_APP_API_URL}/api/login/getprefilleddata`, {
        method: 'POST',
        body: JSON.stringify({
          IncompleteApplicationId: this.props.incompleteApplicationId || parseInt(sessionStorage.getItem('id')),
          Token: this.props.token || sessionStorage.getItem('token')
        }),
        headers: { 'Content-Type': 'application/json' }
      }))
      .then(res => res.json())
      .then(res => {
        if (res.StatusCode === 200) {
          this.setState({ loading: false })
        }
      })
      .catch(err => {
        console.error(err)
        this.setState({ loading: false })
      })
  }

  addFiles (newFiles) {
    const { files, numberOfUpload, maxApplicationAttachmentSize } = this.state
    if (files.length + newFiles.length > numberOfUpload) {
      alert(`You can add up to ${numberOfUpload} files only!`)
      return
    }
    let total = files.reduce((sum, f) => sum + f.size, 0)
    const toAdd = []
    for (const f of newFiles) {
      total += f.size
      if (Math.round((total / 1000000) * 100) / 100 > maxApplicationAttachmentSize) {
        alert('Upload Failed — maximum allowed total attachment size was exceeded!')
        return
      }
      toAdd.push(f)
    }
    this.setState({ files: [...files, ...toAdd] })
  }

  handleDelete (index) {
    const files = [...this.state.files]
    files.splice(index, 1)
    this.setState({ files })
  }

  async toBase64 () {
    const { files } = this.state
    const toB64 = f => new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(f)
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = e => reject(e)
    })

    const b64Array = await Promise.all(files.map(f => toB64(f)))
    return files.map((f, i) => ({
      IncompleteApplicationId: this.props.incompleteApplicationId || sessionStorage.getItem('id'),
      fileName: f.typelabel + '-' + f.name,
      fileExtension: '.' + f.name.split('.').pop(),
      fileContent: b64Array[i],
      label: f.label,
      typelabel: f.typelabel
    }))
  }

  validateMandatory () {
    const { files } = this.state
    const hasLabel = label => files.some(f => f.label === label)
    const missing = []

    if (!hasLabel('BachelorDegreeCertificate')) missing.push('Official Bachelor\'s Degree Certificate')
    if (!hasLabel('AcademicTranscript')) missing.push('Official Academic Transcript')
    const isEgyptian = parseInt(this.props.nationality) === 50
    if (isEgyptian && !hasLabel('NationalID')) missing.push('National ID')
    if (!isEgyptian && !hasLabel('Passport')) missing.push('Valid Passport Copy')
    if (!hasLabel('BirthCertificate')) missing.push('Birth Certificate')
    if (!hasLabel('PersonalPicture')) missing.push('Passport-Sized Photo')
    if (!hasLabel('CV')) missing.push('Résumé / CV')
    if (!hasLabel('PersonalStatement')) missing.push('Personal Statement / Statement of Purpose')
    if (!hasLabel('LetterOfRecommendation')) missing.push('Letters of Recommendation (min. 2 required)')

    // English proficiency: mandatory if not taught in English
    if (this.props.taughtInEnglish === 'No' && this.props.covAlumni !== 'Yes' && !hasLabel('EnglishAssessment')) {
      missing.push('English Language Proficiency Certificate (IELTS/PTE/TOEFL/Duolingo)')
    }

    // Professional Certificates: mandatory if prof exams were filled
    if (this.props.hasProfExam && !hasLabel('ProfessionalCertificate')) {
      missing.push('Professional Certificates / Diplomas (required as you listed professional exams)')
    }

    // Additional Tests (GRE/GMAT): mandatory if prof exams were filled
    if (this.props.hasProfExam && !hasLabel('AdditionalTests')) {
      missing.push('Additional Tests — GRE, GMAT, or other (required as you listed professional exams)')
    }

    // Experience Letter: mandatory if employment data was filled
    if (this.props.employmentStatus === 'Employed' && !hasLabel('ExperienceLetter')) {
      missing.push('Experience Letter (required as you indicated you are employed)')
    }

    return missing
  }

  handleSubmit () {
    const missing = this.validateMandatory()
    if (missing.length > 0) {
      alert('Please upload the following required documents before submitting:\n\n• ' + missing.join('\n• '))
      return
    }

    this.setState({ submitLoading: true, processingPage: true })

    this.toBase64()
      .then(payload => fetch(`${process.env.REACT_APP_API_URL}/api/Submit/SubmitIncompleteApplication`, {
        method: 'POST',
        body: payload.length
          ? JSON.stringify(payload)
          : JSON.stringify([{ IncompleteApplicationId: this.props.incompleteApplicationId || sessionStorage.getItem('id') }]),
        headers: { 'Content-Type': 'application/json' }
      }))
      .then(res => res.json())
      .then(res => {
        if (res.StatusCode === 200) {
          if (res.Data && res.Data.redirect_url) {
            // PayTabs succeeded — neutralize beforeunload, clear session, redirect
            // Main.js sets beforeunload via addEventListener. Override preventDefault
            // on the event prototype temporarily so the browser ignores it.
            window.__payTabsRedirecting = true
            sessionStorage.clear()
            window.location.replace(res.Data.redirect_url)
          } else {
            // PayTabs failed — application saved but payment not completed
            this.setState({ paymentFailed: true, processingPage: false, submitLoading: false, disable: true })
          }
        } else if (res.StatusCode === 409) {
          this.setState({ alreadySubmitted: true, processingPage: false, submitLoading: false })
        } else {
          this.setState({ error: res.Msg || 'An error occurred.', submitLoading: false, processingPage: false, color: 'red' })
        }
      })
      .catch(err => {
        console.error(err)
        this.setState({ error: 'Some error occurred. Please retry.', submitLoading: false, processingPage: false })
      })
  }

  renderUploadRow (inputId, displayLabel, required, extraNote, fileLabel) {
    const { disable, files } = this.state
    const uploadCount = fileLabel ? files.filter(f => f.label === fileLabel).length : 0
    return (
      <>
        <tr>
          <td><b>File:</b></td>
          <td>
            <label htmlFor={inputId} className={disable ? 'input-file disabled' : 'input-file'}>
              <span>
                <i className='fa fa-folder-open-o' aria-hidden='true' />
                <span> {displayLabel}</span>
                {required && <span style={{ color: 'red' }}>*</span>}
              </span>
            </label>
            {uploadCount > 0 && (
              <span style={{ color: 'green', fontSize: 13, marginLeft: 8, fontWeight: 'bold' }}>
                <i className='fa fa-check-circle' aria-hidden='true' /> {uploadCount} uploaded
              </span>
            )}
            <label style={{ fontSize: 13, fontStyle: 'italic', marginLeft: 4, color: required ? 'red' : '#888' }}>
              {required ? '* Required' : '(Optional)'}
            </label>
            {extraNote && (
              <label style={{ fontSize: 12, fontStyle: 'italic', marginLeft: 4, color: '#888' }}>
                {extraNote}
              </label>
            )}
          </td>
        </tr>
        <br />
      </>
    )
  }

  renderHelpIcon (type) {
    return (
      <i
        className='fa fa-question-circle'
        aria-hidden='true'
        title='Click for more info'
        style={{ cursor: 'pointer', color: '#3168cd', marginLeft: 6, fontSize: 15 }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ helpModal: type }) }}
      />
    )
  }

  render () {
    const {
      loading, files, numberOfUpload, maxAttachmentSize, maxApplicationAttachmentSize,
      acceptFiles, accepts, color, error, submitLoading, showModal, disable, helpModal, showSuccessModal, alreadySubmitted, processingPage, paymentFailed
    } = this.state
    const { taughtInEnglish, hasProfExam, nationality, employmentStatus, covAlumni } = this.props
    const englishRequired = taughtInEnglish === 'No' && covAlumni !== 'Yes'

    if (processingPage) {
      return (
        <div id='mainContainer'>
          <div id='designBorder'></div>
          <img src={logo} className='center main-logo' alt='logo' />
          <div id='main-page' style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #3168CE', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }}></div>
            <p style={{ fontSize: 18, fontWeight: 'bold', color: '#3168CE', marginBottom: 12 }}>Processing your application...</p>
            <p style={{ fontSize: 14, color: '#555' }}>Please do not close or refresh this page.</p>
            <p style={{ fontSize: 13, color: '#888', marginTop: 8 }}>You will be redirected to the payment page shortly.</p>
          </div>
        </div>
      )
    }

    if (paymentFailed) {
      return (
        <div id='mainContainer'>
          <div id='designBorder'></div>
          <img src={logo} className='center main-logo' alt='logo' />
          <div id='main-page' style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ fontSize: 18, fontWeight: 'bold', color: '#c57000', marginBottom: 16 }}>Payment Step Could Not Be Completed</p>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>Your application data has been saved successfully. However, the payment step could not be initiated.</p>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6, marginTop: 12 }}>You will receive an email with further instructions to complete your payment and submit your application.</p>
            <p style={{ fontSize: 13, color: '#888', marginTop: 20 }}>If you do not receive an email within 24 hours, please contact the Admissions Office.</p>
          </div>
        </div>
      )
    }

    if (alreadySubmitted) {
      return (
        <div id='mainContainer'>
          <div id='designBorder'></div>
          <img src={logo} className='center main-logo' alt='logo' />
          <div id='main-page' style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ fontSize: 18, fontWeight: 'bold', color: '#3168CE', marginBottom: 16 }}>Your application has already been submitted.</p>
            <p style={{ fontSize: 14, color: '#555' }}>Please check your email for payment instructions.</p>
          </div>
        </div>
      )
    }
    const isEgyptian = parseInt(nationality) === 50

    if (loading) {
      return (
        <div id='mainContainer'>
          <div id='designBorder'>
            <div id='main-page' style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={loader} style={{ marginLeft: 'auto', marginRight: 'auto' }} alt='Loading...' />
            </div>
          </div>
        </div>
      )
    }

    /* ─── File rows gathered for the uploaded files table ─── */
    let total = 0
    const showFiles = []
    files.forEach((f, index) => {
      total += parseFloat(f.size)
      if (Math.round((total / 1000000) * 100) / 100 <= maxApplicationAttachmentSize) {
        showFiles.push(
          <tr key={index}>
            <td>{f.name}</td>
            <td>{f.name.split('.').pop()}</td>
            <td>{Math.round((f.size / 1000000) * 100) / 100}</td>
            <td>{f.typelabel}</td>
            <td>
              <img
                src={require('../delete.png')}
                style={{ width: 15, height: 15, cursor: 'pointer' }}
                onClick={disable ? null : () => this.handleDelete(index)}
                alt='Delete'
              />
            </td>
          </tr>
        )
      }
    })

    return (
      <div id='mainContainer'>
        <div id='designBorder'></div>
        <img src={logo} className='center main-logo' alt='logo' />
        <div id='main-page'>
        <h4 className='app-heading' style={{ marginBottom: 15 }}>Document Uploads</h4>
            <p><strong>Please ensure that all uploaded documents are submitted in English</strong></p>
            <p>You can attach up to {numberOfUpload} file(s). Maximum per file: {maxAttachmentSize} MB. Total maximum: {maxApplicationAttachmentSize} MB.</p>
            <p>If your transcripts/documents consist of several pages, combine them into one PDF file.</p>

            <div onClick={() => this.setState({ showModal: true })} style={{ cursor: 'pointer' }}>
              <p className='file-support-text' style={{ color: '#3168cd', textDecoration: 'underline' }}>
                <i className='fa fa-info-circle' aria-hidden='true' /> View Supported File Types
              </p>
            </div>

            {/* ── All hidden file inputs ─────────────────────────────────────── */}
            <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
              <div>
                {/* Academic */}
                {makeUploadSection('BachelorDegreeCertificate', 'Bachelor Degree Certificate', 'pg-bachelor-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('AcademicTranscript', 'Academic Transcript', 'pg-transcript-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}

                {/* Identification */}
                {makeUploadSection('Passport', 'Passport', 'pg-passport-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('NationalID', 'National ID', 'pg-nationalid-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('BirthCertificate', 'Birth Certificate', 'pg-birth-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}

                {/* English */}
                {makeUploadSection('EnglishAssessment', 'English Language Proficiency', 'pg-english-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('AdditionalTests', 'Additional Tests (GRE/GMAT)', 'pg-addtests-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}

                {/* Personal & Academic Profile */}
                {makeUploadSection('PersonalPicture', 'Passport-Sized Photo', 'pg-photo-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('CV', 'Résumé / CV', 'pg-cv-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('PersonalStatement', 'Personal Statement', 'pg-ps-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('LetterOfRecommendation', 'Letter of Recommendation', 'pg-lor-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}

                {/* Professional */}
                {makeUploadSection('ExperienceLetter', 'Experience Letter', 'pg-exp-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}
                {makeUploadSection('ProfessionalCertificate', 'Professional Certificate/Diploma', 'pg-profcert-input', disable, acceptFiles, maxAttachmentSize, files, numberOfUpload, this.addFiles)}

                {/* ── Upload table (visible rows) ─────────────────────────────── */}
                <table cellPadding='0' cellSpacing='0' className='file-table'>

                  {/* ── ACADEMIC DOCUMENTS ─── */}
                  <thead><tr><td colSpan='2' style={{ fontWeight: 'bold', paddingTop: 12, paddingBottom: 4, borderBottom: '2px solid #3168cd', color: '#3168cd' }}>Academic Documents</td></tr></thead>
                  <tbody>
                    {this.renderUploadRow('pg-bachelor-input', 'Official Bachelor\'s Degree Certificate', true, null, 'BachelorDegreeCertificate')}
                    {this.renderUploadRow('pg-transcript-input', 'Official Academic Transcript', true, null, 'AcademicTranscript')}
                  </tbody>

                  {/* ── IDENTIFICATION ─── */}
                  <thead><tr><td colSpan='2' style={{ fontWeight: 'bold', paddingTop: 16, paddingBottom: 4, borderBottom: '2px solid #3168cd', color: '#3168cd' }}>Identification Documents</td></tr></thead>
                  <tbody>
                    {this.renderUploadRow('pg-passport-input', 'Valid Passport Copy', !isEgyptian, null, 'Passport')}
                    {this.renderUploadRow('pg-nationalid-input', 'National ID', isEgyptian, null, 'NationalID')}
                    {this.renderUploadRow('pg-birth-input', 'Birth Certificate', true, null, 'BirthCertificate')}
                  </tbody>

                  {/* ── ENGLISH PROFICIENCY ─── */}
                  <thead><tr><td colSpan='2' style={{ fontWeight: 'bold', paddingTop: 16, paddingBottom: 4, borderBottom: '2px solid #3168cd', color: '#3168cd' }}>English Language Proficiency</td></tr></thead>
                  <tbody>
                    {this.renderUploadRow(
                      'pg-english-input',
                      'IELTS / PTE / Duolingo / TOEFL Certificate',
                      englishRequired,
                      !englishRequired ? '(If the prior degree was not taught fully in English)' : null,
                      'EnglishAssessment'
                    )}
                    {this.renderUploadRow('pg-addtests-input', 'Additional Tests (GRE, GMAT, or other)', !!hasProfExam, null, 'AdditionalTests')}
                  </tbody>

                  {/* ── PERSONAL & ACADEMIC PROFILE ─── */}
                  <thead><tr><td colSpan='2' style={{ fontWeight: 'bold', paddingTop: 16, paddingBottom: 4, borderBottom: '2px solid #3168cd', color: '#3168cd' }}>Personal &amp; Academic Profile</td></tr></thead>
                  <tbody>
                    {this.renderUploadRow('pg-photo-input', 'Passport-Sized Photo', true, 'This photo will be used for your University ID', 'PersonalPicture')}
                    {this.renderUploadRow('pg-cv-input', 'Resume/CV', true, null, 'CV')}
                    <tr>
                      <td><b>File:</b></td>
                      <td>
                        <label htmlFor='pg-ps-input' className={disable ? 'input-file disabled' : 'input-file'}>
                          <span>
                            <i className='fa fa-folder-open-o' aria-hidden='true' />
                            <span> Personal Statement / Statement of Purpose</span>
                            <span style={{ color: 'red' }}>*</span>
                            {this.renderHelpIcon('personalStatement')}
                          </span>
                        </label>
                        {files.filter(f => f.label === 'PersonalStatement').length > 0 && (
                          <span style={{ color: 'green', fontSize: 13, marginLeft: 8, fontWeight: 'bold' }}>
                            <i className='fa fa-check-circle' aria-hidden='true' /> {files.filter(f => f.label === 'PersonalStatement').length} uploaded
                          </span>
                        )}
                        <label style={{ fontSize: 13, fontStyle: 'italic', marginLeft: 4, color: 'red' }}>* Required</label>
                      </td>
                    </tr>
                    <br />
                    <tr>
                      <td><b>File:</b></td>
                      <td>
                        <label htmlFor='pg-lor-input' className={disable ? 'input-file disabled' : 'input-file'}>
                          <span>
                            <i className='fa fa-folder-open-o' aria-hidden='true' />
                            <span> Letters of Recommendation (minimum of two)</span>
                            <span style={{ color: 'red' }}>*</span>
                            {this.renderHelpIcon('recommendation')}
                          </span>
                        </label>
                        {files.filter(f => f.label === 'LetterOfRecommendation').length > 0 && (
                          <span style={{ color: 'green', fontSize: 13, marginLeft: 8, fontWeight: 'bold' }}>
                            <i className='fa fa-check-circle' aria-hidden='true' /> {files.filter(f => f.label === 'LetterOfRecommendation').length} uploaded
                          </span>
                        )}
                        <label style={{ fontSize: 13, fontStyle: 'italic', marginLeft: 4, color: 'red' }}>* Required</label>
                      </td>
                    </tr>
                    <br />
                  </tbody>

                  {/* ── PROFESSIONAL DOCUMENTS ─── */}
                  <thead><tr><td colSpan='2' style={{ fontWeight: 'bold', paddingTop: 16, paddingBottom: 4, borderBottom: '2px solid #3168cd', color: '#3168cd' }}>Professional Documents (if applicable)</td></tr></thead>
                  <tbody>
                    {this.renderUploadRow('pg-exp-input', 'Experience Letter', employmentStatus === 'Employed', null, 'ExperienceLetter')}
                    {this.renderUploadRow('pg-profcert-input', 'Professional Certificates, Diplomas, or Licenses', !!hasProfExam, null, 'ProfessionalCertificate')}
                  </tbody>

                </table>
              </div>
            </div>

            {error && <p style={{ color: color, fontSize: 12 }}>{error}</p>}

            {showFiles.length
              ? (
                <div>
                  <p className='table-heading'>Your Attachments</p>
                  <div className='document-table-wrapper'>
                    <table cellPadding='0' cellSpacing='0' className='file-table'>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Size (MB)</th>
                          <th>Category</th>
                          <th>Delete</th>
                        </tr>
                      </thead>
                      <tbody>{showFiles}</tbody>
                    </table>
                  </div>
                  <p className='file-size'>Total Size: {Math.round((total / 1000000) * 100) / 100} MB</p>
                </div>
              )
              : <div className='file-size no-file'>You haven&apos;t uploaded any documents yet.</div>
            }

            <button
              className='upload-file-btn save'
              onClick={this.handleSubmit}
              disabled={disable || submitLoading}
            >
              {submitLoading ? <img src={submit} alt='Loading...' style={{ width: 18, height: 18 }} /> : 'Submit Application'}
            </button>

            {/* ── Supported file types modal ─── */}
            {showModal && (
              <div className='modal-bg'>
                <div className='modal modal-sm'>
                  <div className='header' style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', margin: 2 }}>
                    <p>Supported File Types</p>
                    <p onClick={() => this.setState({ showModal: false })} style={{ cursor: 'pointer' }}>x</p>
                  </div>
                  <ul>
                    {accepts.map(el => <li key={el.type} style={{ margin: 2 }}>{el.name}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* ── Help modals ─── */}
            {helpModal === 'personalStatement' && (
              <div className='modal-bg'>
                <div className='modal'>
                  <div className='header' style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', margin: 2 }}>
                    <p><strong>Personal Statement / Statement of Purpose</strong></p>
                    <p onClick={() => this.setState({ helpModal: null })} style={{ cursor: 'pointer' }}>x</p>
                  </div>
                  <div style={{ padding: '0 16px 16px', whiteSpace: 'pre-line', fontSize: 14 }}>
                    {HELP_PERSONAL_STATEMENT}
                  </div>
                </div>
              </div>
            )}

            {helpModal === 'recommendation' && (
              <div className='modal-bg'>
                <div className='modal'>
                  <div className='header' style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', margin: 2 }}>
                    <p><strong>Letters of Recommendation</strong></p>
                    <p onClick={() => this.setState({ helpModal: null })} style={{ cursor: 'pointer' }}>x</p>
                  </div>
                  <div style={{ padding: '0 16px 16px', whiteSpace: 'pre-line', fontSize: 14 }}>
                    {HELP_RECOMMENDATION}
                  </div>
                </div>
              </div>
            )}

            {/* ── Success Modal — shown after successful submission ─── */}
            {showSuccessModal && (
              <div className='modal-bg'>
                <div className='modal'>
                  <div className='header' style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', margin: 2 }}>
                    <p><strong>Application Submitted</strong></p>
                  </div>
                  <div style={{ padding: '0 16px 16px', fontSize: 14, lineHeight: 1.6 }}>
                    <p style={{ color: 'green', fontWeight: 'bold' }}>Your application has been submitted successfully!</p>
                    <p>Thank you for applying. You will be notified once the evaluation process is completed.</p>
                  </div>
                  <div style={{ padding: '0 16px 16px', textAlign: 'right' }}>
                    <button className='save' onClick={() => {
                      sessionStorage.clear()
                      window.location.replace(window.location.pathname.replace(/\/[^/]*$/, '/login'))
                    }}>Ok</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
    )
  }
}

PGUploadDocuments.propTypes = {
  onClick: PropTypes.func.isRequired,
  incompleteApplicationId: PropTypes.number,
  token: PropTypes.string,
  taughtInEnglish: PropTypes.string,
  hasProfExam: PropTypes.bool,
  nationality: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

PGUploadDocuments.defaultProps = {
  taughtInEnglish: '',
  hasProfExam: false
}
